<template>
    <div class="rounded-lg border border-gray-200 bg-white p-4">
        <div class="mb-2">
            <div class="text-sm font-semibold text-gray-700">{{ $t('views.weekOverview.lifeCycle.chartTitle', { date: insertedDate }) }}</div>
            <div class="text-xs text-gray-500">{{ $t('views.weekOverview.lifeCycle.chartTitleDetail', { count: $n(insertedCount || 0), weight: Number.isFinite(avgWeight) ? $n(avgWeight, 'decimal') : '—' }) }}</div>
        </div>
        <GenericChart v-if="details.length" :options="(chartOptions as any)" :expandable="false" :destroy-on-update="true" />
        <p v-else class="py-6 text-center text-xs italic text-gray-400">{{ $t('views.weekOverview.texts.noData') }}</p>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import merge from 'lodash-es/merge';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import { i18n } from '@/plugins/internationalization/i18n';
import type { WeekDataDetails } from '@/types';

const props = defineProps<{
    // One row per week of the selected batch's life cycle, starting at week 0 (insertion week).
    details: WeekDataDetails[];
    insertedDate: string;
    insertedCount: number;
    avgWeight: number;
    // Weaners leave by sale/transfer; the slaughtered series is dropped for them.
    isWeaners?: boolean;
}>();

// Static defaults only (fonts/colours/tooltip) - the batch-dependent parts (categories, series)
// are merged in per render below, since this composable snapshots whatever is passed in once and
// would otherwise go stale when the selected batch changes length.
const staticDefaults = useDefaultChartOptions({}, {}, false);

const chartOptions = computed(() => {
    const base: Record<string, unknown> = merge({} as Record<string, unknown>, staticDefaults.value);

    // x-axis is "number of weeks after insertion", week 0 = the insertion week - a plain
    // category axis over the row index, not the underlying dates; the base default assumes a
    // datetime axis tied to the period picker, which doesn't apply here.
    return merge(base, {
        chart: { height: 320 },
        xAxis: {
            type: 'category',
            categories: props.details.map((_, index) => `${index}`),
            min: null,
            max: null,
            tickInterval: null,
            labels: { rotation: 0, format: '{value}' },
        },
        yAxis: { allowDecimals: false, softMin: 0 },
        legend: { enabled: true },
        tooltip: { shared: true },
        // The shared default's plotOptions tooltip formats `x` as a date (this.x is a week index
        // here, not a timestamp) - override it so the shared tooltip reads "Week N" per series.
        plotOptions: {
            series: {
                tooltip: {
                    pointFormatter(this: any): string {
                        return `<span style="font-size: 10px">${i18n.global.t('views.weekOverview.lifeCycle.tableHeadings.week')} ${this.x}</span><br/><span style="color:${this.color}">●</span> ${this.series.name}: <b>${i18n.global.n(this.y)}</b><br/>`;
                    },
                },
            },
        },
        series: [
            {
                type: 'line',
                name: i18n.global.t('views.weekOverview.lifeCycle.seriesRemaining') as string,
                data: props.details.map((detail) => detail.remaining),
                marker: { enabled: false },
            },
            {
                type: 'column',
                name: i18n.global.t('views.weekOverview.lifeCycle.seriesDead') as string,
                data: props.details.map((detail) => detail.dead),
                borderRadius: 1,
            },
            ...(props.isWeaners
                ? []
                : [
                      {
                          type: 'column',
                          name: i18n.global.t('views.weekOverview.lifeCycle.seriesSlaughtered') as string,
                          data: props.details.map((detail) => detail.slaughtered),
                          borderRadius: 1,
                      },
                  ]),
            {
                type: 'column',
                name: i18n.global.t('views.weekOverview.lifeCycle.seriesTransfered') as string,
                data: props.details.map((detail) => detail.transferred),
                borderRadius: 1,
            },
            {
                type: 'column',
                name: i18n.global.t('views.weekOverview.lifeCycle.seriesDeparted') as string,
                // Weaners have no slaughtered series; their slaughtered exits count as sold here, as in the table/KPIs.
                data: props.details.map((detail) => detail.departed + (props.isWeaners ? (detail.slaughtered ?? 0) : 0)),
                borderRadius: 1,
            },
        ],
    });
});
</script>
